import React, { useState, useEffect, useRef } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, getDay } from 'date-fns';

interface MobileDatePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectDate: (date: Date | null) => void;
  selectedDate: Date | null;
  initialDate?: Date;
}

const MobileDatePicker: React.FC<MobileDatePickerProps> = ({
  isOpen,
  onClose,
  onSelectDate,
  selectedDate,
  initialDate = new Date()
}) => {
  const [currentMonth, setCurrentMonth] = useState(initialDate);
  const prevIsOpenRef = useRef(false);

  // Reset to selectedDate or initialDate only when picker first opens
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      // Picker just opened - initialize the month view
      const dateToUse = selectedDate || initialDate;
      setCurrentMonth(dateToUse);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, initialDate, selectedDate]);

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleSelectToday = () => {
    const today = new Date();
    onSelectDate(today);
  };

  const handleClearDate = () => {
    onSelectDate(null);
  };

  // Calculate days to display in the calendar
  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  // Add empty cells for the days of the week before the first day of the month
  const firstDayOfMonth = getDay(startOfMonth(currentMonth));
  const emptyDays = Array.from({ length: firstDayOfMonth }, (_, i) => i);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Header with back button and title */}
      <div className="flex justify-between items-center p-4 bg-red-500">
        <button onClick={onClose} className="text-white">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h2 className="text-lg font-bold text-white">Select Date</h2>
        <button onClick={handleSelectToday} className="text-white font-medium">
          Today
        </button>
      </div>

      {/* Month navigation */}
      <div className="flex justify-between items-center p-4">
        <button onClick={prevMonth} className="text-gray-600">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="text-lg font-medium text-red-500">{format(currentMonth, 'MMMM yyyy')}</span>
        <button onClick={nextMonth} className="text-gray-600">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 text-center mb-2">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
            <div key={i} className={`text-sm font-medium ${i === 0 || i === 6 ? 'text-blue-500' : 'text-gray-500'}`}>
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for days before the first of the month */}
          {emptyDays.map(i => (
            <div key={`empty-${i}`} className="w-10 h-10"></div>
          ))}

          {/* Days of the month */}
          {daysInMonth.map((day, i) => {
            const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
            const isSelected = selectedDate && format(day, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
            const isWeekend = day.getDay() === 0 || day.getDay() === 6;
            
            return (
              <button
                key={i}
                className={`w-10 h-10 flex items-center justify-center rounded-full text-sm
                  ${isSelected 
                    ? 'bg-blue-500 text-white' 
                    : isToday 
                      ? 'border border-blue-500 text-blue-500' 
                      : isWeekend
                        ? 'text-blue-500'
                        : 'text-gray-800 hover:bg-gray-100'
                  }`}
                onClick={() => onSelectDate(day)}
              >
                {format(day, 'd')}
              </button>
            );
          })}
        </div>
      </div>

      {/* Footer buttons - removed since not in screenshot */}
    </div>
  );
};

export default MobileDatePicker; 